import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Typography, Button, Box } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { unique, arraysEqual } from '../../../../utils/collections';
import { updateStagingPoints, unpublishStagingAction } from '../../../../redux/actions/staging';
import { stagingPointDiff } from '../../staging';
import {
  tableSelectionOptions,
  defaultTableOptions,
  defaultTableInitialState,
} from '../../../util/data-table';
import {
  deviceIdCol,
  pointColumns,
  pointObjectIdentifierId,
  pointNameId,
  pointTypeId,
  equipmentPointsSuperColumn,
  pointDescriptionId,
} from '../../columns';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import { unpublishConfirm } from '../../dialogs/confirmations';

const EquipmentPoints = ({
  stagingPoints,
  equipment,
  pointTypes,
  units,
  equipmentTypes,
  stagingPointUpdates,
  unpublish,
}) => {
  const [confirmation, setConfirmation] = useState({});
  const equipNames = useMemo(
    () => unique(stagingPoints.flatMap(({ equip }) => equip).map(({ name }) => name)),
    [stagingPoints]
  );
  const columns = useMemo(
    () =>
      pointColumns(
        false,
        pointTypes,
        units,
        equipNames,
        equipmentTypes,
        equipmentPointsSuperColumn
      ),
    [equipNames, equipmentTypes, pointTypes, units]
  );
  const data = useMemo(
    // Default order selected first
    () =>
      stagingPoints
        .map((p) => ({
          ...p,
          diff: stagingPointDiff(p, equipment),
        }))
        .sort((a, b) => {
          if (a.equipIds.includes(equipment.equipId)) {
            if (b.equipIds.includes(equipment.equipId)) {
              return a.topic.localeCompare(b.topic);
            }
            return -1;
          }
          if (b.equipIds.includes(equipment.equipId)) return 1;
          return a.topic.localeCompare(b.topic);
        }),
    [stagingPoints, equipment]
  );

  const relatedDevices = unique(equipment.points.map((p) => p.device.deviceId));
  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters:
          relatedDevices.length === 0 ? [] : [{ id: deviceIdCol().id, value: relatedDevices }],

        hiddenColumns: columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![
                equipmentPointsSuperColumn.id,
                pointNameId,
                pointTypeId,
                pointDescriptionId,
                deviceIdCol().id,
                pointObjectIdentifierId,
              ].includes(id)
          ),
        forcedHiddenColumns: equipmentPointsSuperColumn.subColumns.map(({ id }) => id),
      }),
      rowSelection: Object.fromEntries(equipment.points.map((p) => [p.topic, true])),
    },
    getRowId: (p) => p.topic,
    renderTopToolbarCustomActions: ({ table }) => {
      const handleApply = () => {
        const pointSelection = table.getSelectedRowModel().flatRows.map(({ original }) => original);
        const topicSelection = pointSelection.map((p) => p.topic);

        const stagingPointsToAdd = pointSelection.filter(
          (p) => !p.equipIds.includes(equipment.equipId)
        );
        const stagingPointsToRemove = stagingPoints
          .filter((p) => !topicSelection.includes(p.topic))
          .filter((p) => p.equipIds.includes(equipment.equipId));
        function finishUnlinking() {
          return stagingPointUpdates(
            stagingPointsToAdd
              .map((p) => ({ topic: p.topic, equip_ids: p.equipIds.concat([equipment.equipId]) }))
              .concat(
                stagingPointsToRemove.map((p) => ({
                  topic: p.topic,
                  equip_ids: p.equipIds.filter((equipId) => equipId !== equipment.equipId),
                }))
              )
          ).catch((e) =>
            setConfirmation({
              id: 'error',
              content: (
                <Typography sx={{ padding: '10px' }} color="error">
                  {e.message.replaceAll('<br />', '\n')}
                </Typography>
              ),
              showCancel: false,
              confirmText: 'Okay',
              onSuccess: () => Promise.resolve(),
              onExit: () => setConfirmation({}),
            })
          );
        }

        const publishedRelatedPointsToMaybeRemove = stagingPointsToRemove
          .map((p) => p.published)
          .filter(Boolean)
          .filter((p) => p.equipIds.includes(equipment.equipId));
        const pidsToUnpublish = publishedRelatedPointsToMaybeRemove
          .filter((p) => arraysEqual(p.equipIds, [equipment.equipId]))
          .map((p) => p.id);
        const linksToUnpublish = publishedRelatedPointsToMaybeRemove
          .filter((p) => p.equipIds.includes(equipment.equipId))
          .map((p) => p.id);

        if (linksToUnpublish.length > 0)
          return setConfirmation({
            ...unpublishConfirm(
              unpublish,
              [],
              pidsToUnpublish,
              linksToUnpublish.map((pid) => ({
                point_id: pid,
                equipment_id: equipment.publishedEquipmentId,
              })),
              (cont) => (cont ? finishUnlinking() : null)
            ),
            onExit: () => setConfirmation({}),
          });
        return finishUnlinking();
      };

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            color="success"
            disabled={arraysEqual(
              table
                .getSelectedRowModel()
                .flatRows.map(({ original }) => original.topic)
                .toSorted(),
              equipment.points.map((p) => p.topic).toSorted()
            )}
            onClick={handleApply}
            variant="contained"
          >
            Apply Selection
          </Button>
        </Box>
      );
    },
  });
  return (
    <>
      <ConfirmDialog
        id={confirmation.id}
        content={confirmation.content}
        showCancel={confirmation.showCancel}
        confirmText={confirmation.confirmText}
        onSuccess={confirmation.onSuccess}
        onExit={confirmation.onExit}
      />
      <MaterialReactTable table={mrt} />
    </>
  );
};

function mapStateToProps(state) {
  const {
    entities: { equipTypes, pointTypes, units },
  } = state;
  return {
    pointTypes: pointTypes.byId,
    units: units.byId,
    equipmentTypes: equipTypes.byId,
  };
}

function mapDispatchToProps(dispatch, { building }) {
  return {
    stagingPointUpdates: (pointUpdates) => dispatch(updateStagingPoints(building.id, pointUpdates)),
    unpublish: (equipmentIds, pointIds, relationships) =>
      dispatch(unpublishStagingAction(building.id, equipmentIds, pointIds, relationships)),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(EquipmentPoints);
